// ./src/hooks/useFormFields.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  FormField,
  FormFieldService,
  FormFieldFilter,
  FormFieldInput,
  Pagination,
  SortItem,
} from "@/utils/api";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseFormFieldsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<FormFieldFilter>;

  /** Initial sort configuration */
  initialSort?: SortItem[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;
}

export interface UseFormFieldsReturn {
  // Data state
  formField: FormField | null;
  formFields: FormField[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<FormFieldFilter>;
  sortBy: SortItem[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingFormFieldId: string | null;
  selectedFormField: FormField | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<FormFieldFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<FormField[]>;
  create: (params: FormFieldInput) => Promise<FormField | null>;
  update: (id: string, params: FormFieldInput) => Promise<FormField | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<FormFieldFilter>) => void;
  setSortBy: (sort: SortItem[]) => void;

  // UI state management
  setSelectedFormField: (formField: FormField | null) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortItem[] = [{ name: "name", order: "ASC" }];

export const useFormFields = (
  options: UseFormFieldsOptions = {}
): UseFormFieldsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortItem[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [formField, setFormField] = useState<FormField | null>(null);
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<FormFieldFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingFormFieldId, setRemovingFormFieldId] = useState<string | null>(
    null
  );
  const [selectedFormField, setSelectedFormField] = useState<FormField | null>(
    null
  );

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => formFields.length === 0 && !loading,
    [formFields.length, loading]
  );

  // Helper function to update form field in list if it exists, or add it if not
  const updateOrAddFormFieldInList = useCallback(
    (updatedFormField: FormField) => {
      setFormFields((prev) => {
        const existingIndex = prev.findIndex(
          (f) => f.id === updatedFormField.id
        );
        if (existingIndex >= 0) {
          const newFormFields = [...prev];
          newFormFields[existingIndex] = updatedFormField;
          return newFormFields;
        } else {
          return [updatedFormField, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove form field from list if it exists
  const removeFormFieldFromList = useCallback((formFieldId: string) => {
    setFormFields((prev) => prev.filter((f) => f.id !== formFieldId));
  }, []);

  // Helper function to remove multiple form fields from list
  const removeFormFieldsFromList = useCallback((formFieldIds: string[]) => {
    const idsSet = new Set(formFieldIds);
    setFormFields((prev) => prev.filter((f) => !idsSet.has(f.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setFormField(null);
    setFormFields([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingFormFieldId(null);
    setSelectedFormField(null);
  }, []);

  // Get single form field (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await FormFieldService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const formFieldData = result.data[0] as FormField;
          setFormField(formFieldData);
          updateOrAddFormFieldInList(formFieldData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load form field";
          setLastError(errorMessage);
          setFormField(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load form field";
        setLastError(errorMessage);
        setFormField(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddFormFieldInList]
  );

  // List form fields (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<FormFieldFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setFormFields([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) {
        setSortBy(sortByParams);
      }

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const result = await FormFieldService.list(
          filter as FormFieldFilter,
          paginationParams,
          currentSortBy
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newFormFields = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setFormFields((prev) => {
              const existingIds = new Set(prev.map((f) => f.id));
              const uniqueNewFormFields = newFormFields.filter(
                (f: any) => !existingIds.has(f.id)
              );
              return [...prev, ...uniqueNewFormFields];
            });
          } else {
            setFormFields(newFormFields);
          }

          setHasMore(newFormFields.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load form fields";
          setLastError(errorMessage);
          if (!isAppending) setFormFields([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load form fields";
        setLastError(errorMessage);
        if (!isAppending) setFormFields([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many form fields by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<FormField[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await FormFieldService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedFormFields = Array.isArray(result.data)
            ? result.data
            : [];
          loadedFormFields.forEach((formField: any) => {
            updateOrAddFormFieldInList(formField);
          });
          setLastError(null);
          return loadedFormFields;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load form fields";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load form fields";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddFormFieldInList]
  );

  // Create form field (matching API method name)
  const create = useCallback(
    async (params: FormFieldInput): Promise<FormField | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await FormFieldService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newFormField = result.data[0] as FormField;
          setFormField(newFormField);
          setFormFields((prev) => [newFormField, ...prev]);
          setLastError(null);
          return newFormField;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create form field";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create form field";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update form field (matching API method name)
  const update = useCallback(
    async (id: string, params: FormFieldInput): Promise<FormField | null> => {
      if (!id) {
        setLastError("Form field ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await FormFieldService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedFormField = result.data[0] as FormField;
          setFormField(updatedFormField);
          updateOrAddFormFieldInList(updatedFormField);
          setLastError(null);
          return updatedFormField;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update form field";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update form field";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddFormFieldInList]
  );

  // Remove form field (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Form field ID is required for removal");
        return false;
      }

      setRemovingFormFieldId(id);
      setLastError(null);

      try {
        const result = await FormFieldService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setFormField(null);
          removeFormFieldFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove form field";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove form field";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingFormFieldId(null);
      }
    },
    [removeFormFieldFromList]
  );

  // Remove many form fields (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one form field ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await FormFieldService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setFormField(null);
          removeFormFieldsFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove form fields";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove form fields";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeFormFieldsFromList]
  );

  // Load more form fields
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh form fields
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    formField,
    formFields,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingFormFieldId,
    selectedFormField,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedFormField,
    reset,
    lastError,
    clearError,
  };
};
